import React, { memo, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Button,
  Icon,
  TextField,
  Typography,
  Card,
  Checkbox,
  Divider,
  InputAdornment,
  IconButton
} from 'investira.react.components';
import { BaseTemplate } from '../../../components/templates';
import withMessage from '../../../hoc/withMessage';
import { AVALIACAO_SESSOES_QUESTOES } from '../../../enums';
import services from '../../../services';

const AvaliacoesSettingsModeloAdd = memo(props => {
  const navigate = useNavigate();
  const xSessoesQuestoes = Object.values(AVALIACAO_SESSOES_QUESTOES);

  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [buscaQuestao, setBuscaQuestao] = useState('');
  const [questoesSelecionadas, setQuestoesSelecionadas] = useState({});

  const xTotalQuestoes = useMemo(() => {
    return xSessoesQuestoes.reduce(
      (pAcumulado, pSessao) => pAcumulado + pSessao.questoes.length,
      0
    );
  }, [xSessoesQuestoes]);

  const xSessoesFiltradas = useMemo(() => {
    const xBuscaNormalizada = buscaQuestao.trim().toLowerCase();

    return xSessoesQuestoes
      .map(xSessao => {
        const xQuestoesFiltradas = xSessao.questoes.filter(xQuestao => {
          if (!xBuscaNormalizada) {
            return true;
          }

          return xQuestao.questao.toLowerCase().includes(xBuscaNormalizada);
        });

        return { ...xSessao, questoesFiltradas: xQuestoesFiltradas };
      })
      .filter(xSessao => xSessao.questoesFiltradas.length > 0);
  }, [buscaQuestao, xSessoesQuestoes]);

  const xTotalSelecionadas = Object.keys(questoesSelecionadas).length;

  const isQuestaoSelecionada = pQuestaoId => {
    return Boolean(questoesSelecionadas[pQuestaoId]);
  };

  const handleToggleQuestao = pQuestao => {
    setQuestoesSelecionadas(pCurrent => {
      const xIsSelected = Boolean(pCurrent[pQuestao.id]);

      if (xIsSelected) {
        const xNewValue = { ...pCurrent };
        delete xNewValue[pQuestao.id];
        return xNewValue;
      }

      return {
        ...pCurrent,
        [pQuestao.id]: {
          id: pQuestao.id,
          questao: pQuestao.questao,
          sessao_id: pQuestao.sessao_id,
          peso: null
        }
      };
    });
  };

  const handleUpdatePeso = (pQuestaoId, pPeso) => {
    setQuestoesSelecionadas(pCurrent => {
      if (!pCurrent[pQuestaoId]) {
        return pCurrent;
      }

      return {
        ...pCurrent,
        [pQuestaoId]: {
          ...pCurrent[pQuestaoId],
          peso: pPeso
        }
      };
    });
  };

  const handleToggleSessao = pSessao => {
    setQuestoesSelecionadas(pCurrent => {
      const xIdsFiltrados = pSessao.questoesFiltradas.map(xQuestao => xQuestao.id);
      const xAllSelected = xIdsFiltrados.every(xId => Boolean(pCurrent[xId]));

      if (xAllSelected) {
        const xNewValue = { ...pCurrent };
        xIdsFiltrados.forEach(xId => {
          delete xNewValue[xId];
        });
        return xNewValue;
      }

      const xNewValue = { ...pCurrent };
      pSessao.questoesFiltradas.forEach(xQuestao => {
        if (!xNewValue[xQuestao.id]) {
          xNewValue[xQuestao.id] = {
            id: xQuestao.id,
            questao: xQuestao.questao,
            sessao_id: xQuestao.sessao_id,
            peso: null
          };
        }
      });
      return xNewValue;
    });
  };

  const handleSelectTodasFiltradas = () => {
    setQuestoesSelecionadas(pCurrent => {
      const xQuestoesFiltradas = xSessoesFiltradas.flatMap(xSessao => xSessao.questoesFiltradas);
      const xAllSelected =
        xQuestoesFiltradas.length > 0 &&
        xQuestoesFiltradas.every(xQuestao => Boolean(pCurrent[xQuestao.id]));

      if (xAllSelected) {
        const xNewValue = { ...pCurrent };
        xQuestoesFiltradas.forEach(xQuestao => {
          delete xNewValue[xQuestao.id];
        });
        return xNewValue;
      }

      const xNewValue = { ...pCurrent };
      xQuestoesFiltradas.forEach(xQuestao => {
        if (!xNewValue[xQuestao.id]) {
          xNewValue[xQuestao.id] = {
            id: xQuestao.id,
            questao: xQuestao.questao,
            sessao_id: xQuestao.sessao_id,
            peso: null
          };
        }
      });
      return xNewValue;
    });
  };

  const handleScrollToSessao = pSessaoId => {
    const xElement = document.getElementById(`sessao-${pSessaoId}`);

    if (xElement) {
      xElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSave = async () => {
    if (!titulo.trim()) {
      props.onMessageError?.('Informe o título do modelo.');
      return;
    }

    if (xTotalSelecionadas === 0) {
      props.onMessageError?.('Selecione ao menos uma questão para o modelo.');
      return;
    }

    const xPayload = {
      titulo: titulo.trim(),
      descricao: descricao.trim(),
      qtd_questoes: xTotalSelecionadas,
      questoes: Object.values(questoesSelecionadas).sort((a, b) => a.id - b.id)
    };

    try {
      const rRes = await services.mockAvaliacoes.modelosRepo.create(xPayload);

      if (!rRes.ok) {
        props.onMessageError?.(rRes?.error?.message || 'Não foi possível criar o modelo.');
        return;
      }

      props.onMessageSuccess?.('Modelo criado com sucesso.');
      navigate('/avaliacoes/configuracoes/modelos');
    } catch (error) {
      props.onMessageError?.('Não foi possível criar o modelo.');
    }
  };

  return (
    <BaseTemplate
      title={'Novo Modelo'}
      subtitle={'Configurações'}
      description="Utilize modelos para padronizar critérios e estruturar avaliações personalizadas de acordo com as necessidades da sua estratégia. Esses modelos serão a base para organizar as questões e atribuir pesos, facilitando a análise dos resultados."
      maxWidth={'lg'}
      headerRenderActions={
        <Stack direction="row" spacing={2}>
          <Button
            disabled={props.disabledButtons}
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />}
            size="small"
            sx={{ px: 2 }}
            onClick={handleSave}>
            Salvar
          </Button>
        </Stack>
      }>
      <Stack gap={2} py={3}>
        <TextField
          label="Título"
          placeholder="Ex.: Modelo Conservador"
          value={titulo}
          onChange={pEvent => setTitulo(pEvent.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
        />

        <TextField
          label="Descrição"
          placeholder="Descreva o objetivo deste modelo"
          value={descricao}
          onChange={pEvent => setDescricao(pEvent.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          multiline
          rows={4}
        />
      </Stack>

      <Divider />

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={3} py={3}>
        <Stack flexGrow={1} gap={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5}>
            <TextField
              label="Buscar questão"
              placeholder="Busque por palavra-chave no enunciado"
              value={buscaQuestao}
              onChange={pEvent => setBuscaQuestao(pEvent.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon iconName="find" size={21} color={'secondaryLightness'} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size={'small'}
                      onClick={() => setBuscaQuestao('')}
                      disabled={!buscaQuestao.trim()}
                      color={'primary'}>
                      <Icon iconName={'cancel'} size={18} />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleSelectTodasFiltradas}
              sx={{ minWidth: { md: 220 } }}>
              {xSessoesFiltradas.length > 0 &&
              xSessoesFiltradas
                .flatMap(xSessao => xSessao.questoesFiltradas)
                .every(xQuestao => isQuestaoSelecionada(xQuestao.id))
                ? 'Limpar filtradas'
                : 'Selecionar filtradas'}
            </Button>
          </Stack>

          {xSessoesFiltradas.length === 0 && (
            <Card>
              <Stack py={3} px={2}>
                <Typography variant="body2" color="textSecondary">
                  Nenhuma questão encontrada para o filtro aplicado.
                </Typography>
              </Stack>
            </Card>
          )}

          {xSessoesFiltradas.map(xSessao => {
            const xTotalSessao = xSessao.questoesFiltradas.length;
            const xTotalSelecionadasSessao = xSessao.questoesFiltradas.filter(xQuestao =>
              isQuestaoSelecionada(xQuestao.id)
            ).length;
            const xAllSelected = xTotalSessao > 0 && xTotalSelecionadasSessao === xTotalSessao;

            return (
              <Card
                key={xSessao.sessao_id}
                id={`sessao-${xSessao.sessao_id}`}
                sx={{ scrollMarginTop: 88 }}>
                <Stack p={2} gap={2}>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                    gap={1}>
                    <Stack>
                      <Typography variant="h6" color="textPrimary">
                        {xSessao.sessao}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {xTotalSelecionadasSessao} de {xTotalSessao} selecionadas
                      </Typography>
                    </Stack>

                    <Button variant="text" size="small" onClick={() => handleToggleSessao(xSessao)}>
                      {xAllSelected ? 'Limpar sessão' : 'Selecionar sessão'}
                    </Button>
                  </Stack>

                  <Divider />

                  <Stack gap={1.5}>
                    {xSessao.questoesFiltradas.map(xQuestao => {
                      const xSelecionada = isQuestaoSelecionada(xQuestao.id);

                      return (
                        <Stack
                          key={xQuestao.id}
                          gap={1}
                          sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 3 }}>
                          <Stack direction="row" gap={1} alignItems="center" flexGrow={1}>
                            <Checkbox
                              checked={xSelecionada}
                              onChange={() => handleToggleQuestao(xQuestao)}
                              size="small"
                              sx={{ mt: 0.2 }}
                            />
                            <Stack direction="row" justifyContent="space-between" flexGrow={1}>
                              <Typography color="textPrimary" variant="body2">
                                {xQuestao.questao}
                              </Typography>
                              <Typography color="textSecondary" variant="caption">
                                ID #{xQuestao.id}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>

        <Stack
          minWidth={{ lg: 280 }}
          maxWidth={{ lg: 320 }}
          width="100%"
          gap={2}
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 16 },
            alignSelf: { lg: 'flex-start' }
          }}>
          <Card>
            <Stack p={2} gap={1}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ textTransform: 'uppercase' }}>
                Sessões
              </Typography>

              {xSessoesFiltradas.map(xSessao => {
                // const xTotalSessao = xSessao.questoesFiltradas.length;
                // const xSelecionadasSessao = xSessao.questoesFiltradas.filter(xQuestao =>
                //   isQuestaoSelecionada(xQuestao.id)
                // ).length;

                return (
                  <Button
                    key={xSessao.sessao_id}
                    variant="text"
                    size="small"
                    onClick={() => handleScrollToSessao(xSessao.sessao_id)}
                    sx={{ justifyContent: 'start', px: 1, textTransform: 'none' }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      width="100%"
                      justifyContent="flex-start">
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ textAlign: 'left', flexGrow: 1 }}>
                        {xSessao.sessao}
                      </Typography>
                      {/* <Typography variant="caption" color="textSecondary">
                        {xSelecionadasSessao}/{xTotalSessao}
                      </Typography> */}
                    </Stack>
                  </Button>
                );
              })}
            </Stack>
          </Card>

          <Card>
            <Stack p={2} gap={1}>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ textTransform: 'uppercase' }}>
                Resumo do modelo
              </Typography>
              <Typography variant="body2" color="textPrimary">
                Questões selecionadas: {xTotalSelecionadas}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total disponível: {xTotalQuestoes}
              </Typography>
            </Stack>
          </Card>
          <Stack spacing={2}>
            <Button
              disabled={props.disabledButtons}
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="upload" size={16} />}
              size="small"
              sx={{ px: 2 }}
              onClick={handleSave}>
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BaseTemplate>
  );
});

AvaliacoesSettingsModeloAdd.displayName = 'AvaliacoesSettingsModeloAdd';

export default withMessage(AvaliacoesSettingsModeloAdd);
